// Constats d'un camion : une ligne courte par écart, avec son impact en minutes, triés par coût.
import { driveLegs, fmtDelta, fmtDistance, fmtDuration, fmtTime } from "./geotabFormat.js";

export const FLEET = "__fleet";
const kind = (k) => (k === "loading" ? "Chargement" : "Livraison");

// Ce qui coûte d'abord (minutes au-delà du plan), puis ce qui est à vérifier, puis les gains.
// Un site non visité ou un autre camion pèse comme une heure d'écart.
function rank(item) {
  if (item.cat === "Sites" || item.cat === "Camion") return 3e4 + 60;
  if (item.impact > 0) return 3e4 + item.impact;
  if (item.impact == null) return 1e4;
  return -Math.abs(item.impact);
}

// now : minute du jour pour la journée en cours ; un site pas encore atteint n'est pas « non vu ».
export function truckItems(row, { tolerance = 15, now = Infinity } = {}) {
  const c = row.comparison;
  const p = row.plan;
  if (!c || !p) return [];
  const items = [];
  const others = (row.trucks_used || []).filter((u) => !u.planned);
  if (others.length) {
    items.push({ cat: "Camion", tone: "warn", badge: "Camion", title: "Autre camion utilisé", detail: (row.trucks_used || []).map((u) => `${u.name} ${fmtTime(u.start)}–${fmtTime(u.end)}`).join(", ") });
  }
  const sites = c.sites.filter((s) => s.kind !== "depot");
  const missing = sites.filter((s) => s.real_arrival == null && s.plan_arrival + tolerance < now);
  if (missing.length) {
    const plural = missing.length > 1 ? "s" : "";
    items.push({ cat: "Sites", tone: "bad", badge: `${missing.length} non vu${plural}`, title: `${missing.length} site${plural} sans arrêt Geotab`, detail: missing.map((s) => `${s.name} (${fmtTime(s.plan_arrival)})`).join(", ") });
  }
  sites.filter((s) => s.real_arrival != null).forEach((s) => {
    const d = s.real_dwell - s.plan_dwell;
    if (s.real_dwell != null && Math.abs(d) > tolerance) items.push({ cat: "Temps sur place", impact: d, title: s.name, detail: `${kind(s.kind)} : ${fmtDuration(s.real_dwell)} sur place au lieu de ${fmtDuration(s.plan_dwell)}` });
    if (s.match === "address" || s.match === "place") items.push({ cat: "Adresses", tone: "warn", badge: fmtDistance(s.offset_m), title: s.name, detail: `Arrêt à ${fmtDistance(s.offset_m)} de l'adresse prévue` });
  });
  // Route : trajets comparables seulement, comme « Durées par étape ». Un camion encore en route, un ordre modifié ou un site
  // non vu ne gonflent plus le temps de route.
  const legs = driveLegs(row);
  const done = legs.filter((l) => l.real != null);
  const [planDrive, realDrive] = ["plan", "real"].map((k) => done.reduce((sum, l) => sum + l[k], 0));
  if (Math.abs(realDrive - planDrive) > tolerance) {
    items.push({ cat: "Route", impact: realDrive - planDrive, title: "Temps de route",
      detail: `${fmtDuration(realDrive)} au lieu de ${fmtDuration(planDrive)}${done.length < legs.length ? ` · ${done.length} sur ${legs.length} trajets` : ""}` });
  }
  c.off_stops.forEach((v) => items.push({
    cat: ["off", "depot"].includes(v.kind) && !v.switch ? "Hors plan" : "Autre arrêt", impact: v.dwell, tone: "off",
    title: v.switch ? "Changement de camion" : v.kind === "depot" ? "Dépôt · hors plan" : v.kind === "loading" ? `Chargement ${v.name}` : "Arrêt hors plan",
    detail: `à ${fmtTime(v.arr)} · ${v.place?.name || `${v.lat.toFixed(3)}, ${v.lon.toFixed(3)}`}`,
  }));
  // Pas de constat sur l'heure de départ ni de retour : un chauffeur peut partir à 5 h ou à 7 h. Seul un retour ailleurs est signalé.
  if (c.return_delta == null && !c.in_progress) items.push({ cat: "Dépôt", tone: "warn", badge: "Ailleurs", title: "Fin de journée hors du dépôt", detail: `${fmtTime(c.end_at)}, à ${fmtDistance(c.end_distance_m)} du dépôt` });
  if (!c.order_ok) items.push({ cat: "Ordre", tone: "warn", badge: "Ordre", title: "Ordre des sites modifié", detail: "Trajets marqués « ordre modifié » dans Durées par étape" });
  return items.sort((a, b) => rank(b) - rank(a));
}

// Tous les camions : leurs constats, préfixés du chauffeur, triés ensemble.
export function fleetItems(rows, options) {
  return rows.flatMap((row) => truckItems(row, options).map((item) => ({ ...item, title: `${row.driver_plan || row.driver_geotab || row.truck_name} · ${item.title}` })))
    .sort((a, b) => rank(b) - rank(a));
}

export const impactLabel = (item) => item.badge || fmtDelta(item.impact);
